import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IAddressValidationResponse, IAddVoucherHolderResponse, IApplicationState } from "../";
import { IGet, IPost } from '../authorization';
import { IBaseResponse, IDonor, IDonorResponse, IFileUpload, ICheekSwabRequest, IDonorWithMSRecipientUpdated } from '../../models';
import { AddressValidationResult } from '@nkr/enums/address-validation-result';
import { ActionCreators as ActionCreatorsProfileAdmin, IDonorProfileAdminResponse, } from './actions-donor-profile-admin';
import { ActionCreators as ActionCreatorsProfileDonorData, IDonorProfileDonorDataResponse, IDonorProfileDonorDataLookupDataResponse } from './actions-donor-profile-donor-data';
import { ActionCreators as ActionCreatorsProfileMedicalDetails, IDonorProfileMedicalDetailsResponse, IDonorProfileMedicalDetailsLookupDataResponse } from './actions-donor-profile-medical-details';
import { ActionCreators as ActionCreatorsProfileHlaInfo, IDonorProfileHlaInfoResponse, IDonorProfileHlaInfoLookupDataResponse, IDonorProfileHlaInfoPostDataResponse } from './actions-donor-profile-hla-info';
import { ActionCreators as ActionCreatorsProfileRecords, IDonorProfileRecordsResponse } from './actions-donor-profile-records';
import { ActionCreators as ActionCreatorsProfileCtScan, IDonorProfileCtScanResponse, IAmbraDirectToViewerResponse } from './actions-donor-profile-ct-scan';
import { ActionCreators as ActionCreatorsProfileAcceptsDeclines, IDonorProfileDeclinesResponse, IDonorProfileAcceptsResponse } from './actions-donor-profile-accepts-declines';
import { ActionCreators as ActionCreatorsHeaderInfo, IDonorProfileHeaderInfoResponse } from './actions-donor-profile-header-info';
import { ActionCreators as ActionCreatorsProfileAddress, IDonorProfileDonorAddressLookupDataResponse, IDonorProfileDonorAddressPostResponse, IDonorProfileDonorAddressResponse } from './actions-donor-profile-address';
import { ActionCreators as ActionCreatorsProfilePostTransplant, IDonorProfilePostTransplantResponse } from './actions-donor-profile-post-transplant';
import { ActionCreators as ActionCreatorsVouchers, IDonorProfileVoucherListResponse } from './actions-donor-profile-vouchers';

export interface IDonorAliasResponse {
    data: {
        success: boolean,
        message: string,
        results: string
    }
}

export interface IDonorMinDetailsResponse {
    data: {
        success: boolean,
        results: ICheekSwabRequest
    }
}

export interface IDonorProfileUploadResponse {
    data: {
        success: boolean
    }
}

export interface IDonorProfileOrderCheekSwabResponse {
    data: boolean
}

export {
    ActionCreators as ActionCreatorsFollowUp,
    IFollowUpSurveyCountsResponse, IFollowUpSurveyListResponse, IFollowUpSurveyResponse, IFollowUpDonorListResponse
} from './actions-follow-up';

export {
    ActionCreators as ActionCreatorsFollowUpDonors, IExistingDashRegistration, IExistingDonor,
    IFollowUpDonorResponse,
    IFollowUpDonorLookupDataResponse, IFollowUpDonorVerifyEmailResponse, IFollowUpDonorRecordsByEmailResponse, IFollowUpDonorSaveResponse
} from './actions-follow-up-donor';

export {
    ActionCreators as ActionCreatorsFollowUpReports,
    IFollowUpComplianceReportResponse, IFollowUpReconciliationReportResponse
} from './actions-follow-up-reports';

export {
    ActionCreators as ActionCreatorsNewDonorRecipient,
    INewDonorRecipientlookupDataResponse, ICheckForExistingAliasAndMrnResponse, IExistingMatch, INewDonorRecipientSaveResponse
} from './actions-new-donor-recipient';

const ActionCreatorsMain = {
    getDonorAliasById: (donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorAliasResponse> => {
        let options: IGet = {
            url: `${__API__}/donor/${donorId}/alias-only`,
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getDonorMinById: (donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorMinDetailsResponse> => {
        let options: IGet = {
            url: `${__API__}/donor/${donorId}/basic-info`,
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    uploadDonorConsentForm: (donorId: number, consentFile: File) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('consentFile', consentFile);

        var config: IPost = {
            url: `${__API__}/donor/${donorId}/upload-consent-file/`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(config)).then((response: IBaseResponse) => {
            return response;
        });
    },
    updateDonor: (donor: IDonor) => (dispatch: Dispatch<IApplicationState>): Promise<IDonorResponse> => {
        let options: IPost = {
            url: `${__API__}/donor/update`,
            data: {
                id: donor.id,
                firstName: donor.firstName,
                lastName: donor.lastName,
                address1: donor.address1,
                address2: donor.address2,
                city: donor.city,
                state: donor.state,
                zip: donor.zip,
                country: donor.country,
                phone1: donor.phone1,
                phone2: donor.phone2,
                email: donor.email,
                voucherHolders: donor.adpVoucherHolders
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    getDonorWithVoucherHolders: (donorId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IDonorResponse> => {
        let options = {
            url: __API__ + `/donor/${donorId}/voucher-holders/`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    updateSurgeon: (donorId: number, contactId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/donor/surgeon?donorId=${donorId}&contactId=${contactId}`
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    updateInternalDirectDonor: (donorId: number, donationDate: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/donor/${donorId}/update-internal-direct?donationDate=${donationDate}`
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    uploadDonorProfileDocument: (centerId: number, donorId: number, docType: number, file: IFileUpload, append: boolean = false) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorProfileUploadResponse> => {
        let formData = new FormData;
        formData.append('docType', docType.toString());
        formData.append('file', file.file[0]);
        formData.append('append', append.toString())

        var options = {
            url: `${__API__}/center/${centerId}/donor/${donorId}/profile-upload`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(options));
    },
    updateDonorWithMSPatient: (donorWithMSPatients: IDonorWithMSRecipientUpdated[]) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/donor/update-donor-with-remote-patient`,
            data:
                donorWithMSPatients

        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    postCheekSwabskit: (donorId: number, firstName: string, lastName: string, address1: string, address2: string, city: string, state: string, postalCode: string,
        contactPhone: string, email: string, dob: string
    ) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorProfileOrderCheekSwabResponse> => {
        let options: IPost = {
            url: `${__API__}/orders/cheek-swab/donor/${donorId}`,
            data: {
                firstName,
                lastName,
                address1,
                address2,
                city,
                state,
                postalCode,
                email,
                dob,
                contactPhone,
            }
        }

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    postValidateAddress: (centerId: number, donorId: number, address: string, city: string, state: string, postalCode: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddressValidationResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/donor/${donorId}/validate-address`,
            data: {
                address,
                city,
                state,
                postalCode
            }
        }

        return await dispatch(AuthActionCreators.apiPost(options));
    }
}

// export interface IAddressValidationResponse {
//     data: {
//         success: boolean,
//         results: IUsPsAddressResponse
//     }
// }

// export interface IUsPsAddressResponse {
//     address1: string,
//     address2: string,
//     city: string,
//     state: string,
//     stateName: string,
//     zip5: string,
//     zip4: string,
//     result: AddressValidationResult,
//     resultText: string
// }

export interface IDonorState {
}

var initialState: IDonorState = {
};

export const ActionCreators = {
    ...ActionCreatorsMain,
    ...ActionCreatorsProfileAdmin,
    ...ActionCreatorsProfileDonorData,
    ...ActionCreatorsProfileMedicalDetails,
    ...ActionCreatorsProfileHlaInfo,
    ...ActionCreatorsProfileRecords,
    ...ActionCreatorsProfileCtScan,
    ...ActionCreatorsProfileAcceptsDeclines,
    ...ActionCreatorsHeaderInfo,
    ...ActionCreatorsProfileAddress,
    ...ActionCreatorsProfilePostTransplant,
    ...ActionCreatorsVouchers
};

export {
    IDonorProfileAdminResponse,
    IDonorProfileDonorDataResponse,
    IDonorProfileDonorDataLookupDataResponse,
    IDonorProfileMedicalDetailsResponse,
    IDonorProfileMedicalDetailsLookupDataResponse,
    IDonorProfileHlaInfoResponse,
    IDonorProfileHlaInfoLookupDataResponse,
    IDonorProfileHlaInfoPostDataResponse,
    IDonorProfileRecordsResponse,
    IDonorProfileCtScanResponse,
    IAmbraDirectToViewerResponse,
    IDonorProfileDeclinesResponse,
    IDonorProfileAcceptsResponse,
    IDonorProfileHeaderInfoResponse,
    IDonorProfileDonorAddressResponse,
    IDonorProfileDonorAddressPostResponse,
    IDonorProfileDonorAddressLookupDataResponse,
    IDonorProfilePostTransplantResponse,
    IDonorProfileVoucherListResponse,
    // IVoucherLookupDataResponse, IVoucherHolderRequest
}

const createReducer = (initialState: IDonorState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
};

export const Reducer = createReducer(initialState, handlers);
